/**
 * Service Fee Composable
 * Reaktive Service Fee Verwaltung mit Cent-Support
 */
import { ref, computed } from 'vue';
import MoneyUtils from '@shared/utils/MoneyUtils';
export function useServiceFee() {
    // State
    const serviceFeeCalculation = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    // Computed Properties
    const serviceFeeDisplay = computed(() => {
        if (!serviceFeeCalculation.value)
            return null;
        return MoneyUtils.formatServiceFeeForDisplay(serviceFeeCalculation.value);
    });
    const serviceFeeAmount = computed(() => {
        return serviceFeeCalculation.value?.amountCent || 0;
    });
    const serviceFeeFormatted = computed(() => {
        return serviceFeeDisplay.value?.formatted || MoneyUtils.formatCurrency(0);
    });
    const serviceFeeDescription = computed(() => {
        return serviceFeeDisplay.value?.description || 'Keine Service-Gebühr';
    });
    const isServiceFeeActive = computed(() => {
        return serviceFeeDisplay.value?.isActive || false;
    });
    const serviceFeeType = computed(() => {
        return serviceFeeCalculation.value?.type || 'none';
    });
    // Methods
    const setServiceFee = (calculation) => {
        serviceFeeCalculation.value = calculation;
        error.value = null;
    };
    const clearServiceFee = () => {
        serviceFeeCalculation.value = null;
        error.value = null;
    };
    const setError = (errorMessage) => {
        error.value = errorMessage;
        serviceFeeCalculation.value = null;
    };
    const setLoading = (loading) => {
        isLoading.value = loading;
    };
    // Hilfsmethoden für UI
    const formatServiceFeeForCheckout = computed(() => {
        if (!isServiceFeeActive.value) {
            return '';
        }
        return `Service-Gebühr: ${serviceFeeFormatted.value}`;
    });
    const getServiceFeeTooltip = computed(() => {
        if (!serviceFeeCalculation.value || !isServiceFeeActive.value) {
            return 'Keine Service-Gebühr';
        }
        const details = serviceFeeCalculation.value.details;
        let tooltip = serviceFeeDescription.value;
        if (details.percentage && details.percentage > 0) {
            tooltip += `\nBerechnet: ${details.percentage}% von ${MoneyUtils.formatCurrency(details.subtotalCent)}`;
        }
        if (details.cappedByMax && details.maxValueCent) {
            tooltip += `\nMaximaler Betrag: ${MoneyUtils.formatCurrency(details.maxValueCent)}`;
        }
        return tooltip;
    });
    // Debug helpers (Development only)
    const debugInfo = computed(() => {
        if (process.env.NODE_ENV !== 'development' || !serviceFeeCalculation.value) {
            return null;
        }
        return {
            calculation: serviceFeeCalculation.value,
            display: serviceFeeDisplay.value,
            formattedAmounts: {
                cent: serviceFeeAmount.value,
                euro: MoneyUtils.centToEuro(serviceFeeAmount.value),
                formatted: serviceFeeFormatted.value
            }
        };
    });
    return {
        // State
        serviceFeeCalculation: serviceFeeCalculation,
        isLoading: isLoading,
        error: error,
        // Computed
        serviceFeeDisplay,
        serviceFeeAmount,
        serviceFeeFormatted,
        serviceFeeDescription,
        isServiceFeeActive,
        serviceFeeType,
        formatServiceFeeForCheckout,
        getServiceFeeTooltip,
        // Methods
        setServiceFee,
        clearServiceFee,
        setError,
        setLoading,
        // Debug (Development only)
        ...(process.env.NODE_ENV === 'development' && { debugInfo })
    };
}
export default useServiceFee;
